import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

interface QuickStatsCardProps {
  icon: LucideIcon;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: string;
  trendType?: 'positive' | 'negative' | 'neutral';
  badge?: string;
  badgeType?: 'success' | 'warning' | 'info';
  progress?: number;
  action?: ReactNode;
}

export function QuickStatsCard({
  icon: Icon,
  title,
  value,
  subtitle,
  trend,
  trendType = 'neutral',
  badge,
  badgeType = 'success',
  progress,
  action,
}: QuickStatsCardProps) {
  const trendColors = {
    positive: 'text-success',
    negative: 'text-destructive',
    neutral: 'text-muted-foreground',
  };

  const badgeColors = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/20',
    info: 'bg-info/10 text-info border-info/20',
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-sm border border-border hover:shadow-md transition-all duration-200">
      {/* Icon and Title */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          </div>
        </div>
        {badge && (
          <span
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full border',
              badgeColors[badgeType]
            )}
          >
            {badge}
          </span>
        )}
      </div>

      {/* Value */}
      <div className="mb-2">
        <div className="text-3xl font-bold text-foreground">{value}</div>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="text-sm text-muted-foreground mb-2">{subtitle}</div>
      )}

      {/* Progress Bar */}
      {progress !== undefined && (
        <div className="w-full bg-muted rounded-full h-2 mb-3">
          <div
            className="bg-success h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Trend */}
      {trend && (
        <div className={cn('text-sm font-medium', trendColors[trendType])}>
          {trend}
        </div>
      )}

      {/* Action */}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
